import { useQuery } from "@tanstack/react-query";
import type { ModuleRow } from "@/modules/apps/ModulesManager";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import {
  invokeEdgeFunction,
  readEdgeFunctionErrorMessage,
} from "@/components/atomic-crm/providers/supabase/invokeEdgeFunction";

/**
 * Platform console data (Phase 1, docs/plans/MULTI-TENANT-MODULES.md).
 * Everything goes through operator-only, audited database functions
 * (platform_* in 20261012180000_platform_console.sql) or the platform_admin
 * edge function — never through direct table access.
 */

export type PlatformCompany = {
  id: number;
  name: string;
  created_at: string;
  disabled_at: string | null;
  billing_status: string | null;
  member_count: number;
  admin_emails: string[];
  is_home: boolean;
};

export type PlatformCompanyMember = {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  administrator: boolean;
  disabled: boolean | null;
};

export type PlatformCompanyDetail = {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  website: string | null;
  created_at: string;
  disabled_at: string | null;
  billing_status: string | null;
  stripe_credential_mode: string | null;
  is_home: boolean;
  members: PlatformCompanyMember[];
};

export type PlatformCompanySnapshot = {
  counts: Record<
    "companies" | "contacts" | "deals" | "tasks" | "tickets",
    number
  >;
  recent_companies: Array<{ id: number; name: string; created_at: string }>;
  recent_contacts: Array<{ id: number; name: string; title: string | null }>;
  recent_deals: Array<{
    id: number;
    name: string;
    stage: string | null;
    created_at: string;
  }>;
  recent_tickets: Array<{
    id: number;
    subject: string;
    status: string | null;
    created_at: string;
  }>;
  recent_tasks: Array<{ id: number; text: string; created_at: string }>;
};

export type PlatformAuditEntry = {
  id: number;
  operator_user_id: string;
  org_id: number | null;
  action: string;
  details: Record<string, unknown>;
  created_at: string;
};

const unwrap = <T>({ data, error }: { data: T | null; error: unknown }) => {
  if (error) {
    throw new Error(
      (error as { message?: string }).message ?? "Platform request failed",
    );
  }
  return data as T;
};

export const usePlatformOperator = () =>
  useQuery({
    queryKey: ["platform", "is_operator"],
    queryFn: async () =>
      unwrap<boolean>(await supabase.rpc("is_platform_operator")) === true,
    staleTime: 5 * 60_000,
  });

export const usePlatformCompanies = () =>
  useQuery({
    queryKey: ["platform", "companies"],
    queryFn: async () =>
      unwrap<PlatformCompany[]>(
        await supabase.rpc("platform_list_organizations"),
      ) ?? [],
  });

export const usePlatformCompany = (orgId: number) =>
  useQuery({
    queryKey: ["platform", "company", orgId],
    queryFn: async () =>
      unwrap<PlatformCompanyDetail | null>(
        await supabase.rpc("platform_get_organization", { p_org_id: orgId }),
      ),
    enabled: Number.isFinite(orgId),
  });

export const usePlatformAuditLog = (orgId: number) =>
  useQuery({
    queryKey: ["platform", "audit", orgId],
    queryFn: async () =>
      unwrap<PlatformAuditEntry[]>(
        await supabase
          .from("platform_audit_log")
          .select("*")
          .eq("org_id", orgId)
          .order("created_at", { ascending: false })
          .limit(50),
      ) ?? [],
    enabled: Number.isFinite(orgId),
  });

/** Audited: every call writes a "view_as_company" entry. */
export const viewCompanyAsOperator = async (orgId: number) =>
  unwrap<PlatformCompanySnapshot>(
    await supabase.rpc("platform_view_organization", { p_org_id: orgId }),
  );

export const setCompanyDisabled = async (orgId: number, disabled: boolean) =>
  unwrap<null>(
    await supabase.rpc("platform_set_organization_disabled", {
      p_org_id: orgId,
      p_disabled: disabled,
    }),
  );

const callPlatformAdmin = async <T>(body: Record<string, unknown>) => {
  const { data, error } = await invokeEdgeFunction<T>("platform_admin", {
    method: "POST",
    body,
  });
  if (error) {
    throw new Error(
      await readEdgeFunctionErrorMessage(error, "Platform action failed"),
    );
  }
  return data as T;
};

export const createCompany = (input: {
  company_name: string;
  admin_email: string;
  admin_first_name?: string;
  admin_last_name?: string;
}) =>
  callPlatformAdmin<{ org_id: number }>({
    action: "create_company",
    ...input,
  });

export const resendCompanyAdminInvite = (orgId: number) =>
  callPlatformAdmin<{ sent_to: string }>({
    action: "resend_admin_invite",
    org_id: orgId,
  });

export const usePlatformCompanyModules = (orgId: number) =>
  useQuery({
    queryKey: ["platform", "modules", orgId],
    queryFn: async () =>
      unwrap<ModuleRow[]>(
        await supabase.rpc("platform_get_org_modules", { p_org_id: orgId }),
      ) ?? [],
    enabled: Number.isFinite(orgId),
  });

/** Audited: writes enable_module / disable_module for the company. */
export const setCompanyModule = async (
  orgId: number,
  moduleId: string,
  enabled: boolean,
  options?: Record<string, unknown>,
) =>
  unwrap<null>(
    await supabase.rpc("platform_set_org_module", {
      p_org_id: orgId,
      p_module_id: moduleId,
      p_enabled: enabled,
      p_options: options ?? null,
    }),
  );

export type ViewAsCompanySession = {
  org_id: number;
  org_name: string;
  expires_at: string;
};

/** Active "View as company" session of the signed-in operator, if any. */
export const fetchViewAsCompanySession = async () => {
  const rows = unwrap<ViewAsCompanySession[]>(
    await supabase.rpc("platform_current_view_session"),
  );
  return rows?.[0] ?? null;
};

/** The operator's active "View as company" session, if any. */
export const useViewAsCompanySession = () => {
  const { data: isOperator } = usePlatformOperator();
  return useQuery({
    queryKey: ["platform", "view_session"],
    queryFn: fetchViewAsCompanySession,
    enabled: isOperator === true,
    refetchInterval: 60_000,
  });
};

/** Audited. Reload the app afterwards so every screen reads as the company. */
export const startViewAsCompany = async (orgId: number) =>
  unwrap<null>(
    await supabase.rpc("platform_start_view_session", { p_org_id: orgId }),
  );

export const endViewAsCompany = async () =>
  unwrap<number | null>(await supabase.rpc("platform_end_view_session"));
